import { ArrowRightLeft, Bug, Check, ChevronDown, FileSearch, House, ListChecks, MessagesSquare, PenLine, type LucideIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMe } from "@/hooks/queries/use-me";
import { useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import {
  ASK_PATH,
  ASK_TOOL_NAME,
  FOGBUGZ_FORMULATOR_PATH,
  FOGBUGZ_FORMULATOR_TOOL_NAME,
  FOGBUGZ_PATH,
  FOGBUGZ_TO_ADO_PATH,
  FOGBUGZ_TO_ADO_TOOL_NAME,
  FOGBUGZ_TOOL_NAME,
  FORMULATOR_PATH,
  TOOLBOX_HOME_PATH,
  type ToolboxTool,
  fogbugzToAdoVisible,
  toolFromPath,
} from "@/lib/toolbox/tools";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

interface ToolEntry {
  tool: Exclude<ToolboxTool, "home">;
  label: string;
  href: string;
  icon: LucideIcon;
}

const GROOMING_ENTRY: ToolEntry = { tool: "grooming", label: "Grooming", href: "/grooming", icon: ListChecks };
const FORMULATOR_ENTRY: ToolEntry = { tool: "formulator", label: "ADO Formulator", href: FORMULATOR_PATH, icon: PenLine };
const FOGBUGZ_FORMULATOR_ENTRY: ToolEntry = {
  tool: "fogbugz-formulator",
  label: FOGBUGZ_FORMULATOR_TOOL_NAME,
  href: FOGBUGZ_FORMULATOR_PATH,
  icon: Bug,
};
const FOGBUGZ_ENTRY: ToolEntry = { tool: "fogbugz", label: FOGBUGZ_TOOL_NAME, href: FOGBUGZ_PATH, icon: FileSearch };
const ASK_ENTRY: ToolEntry = { tool: "ask", label: ASK_TOOL_NAME, href: ASK_PATH, icon: MessagesSquare };
const FOGBUGZ_TO_ADO_ENTRY: ToolEntry = {
  tool: "fogbugz-to-ado",
  label: FOGBUGZ_TO_ADO_TOOL_NAME,
  href: FOGBUGZ_TO_ADO_PATH,
  icon: ArrowRightLeft,
};

/**
 * The tools the switcher lists (E283): Grooming while `groomingVisible` (a
 * Toolbox role on some project, or Admin), the Formulator with `formulator`
 * on some project, the FogBugz Formulator (epic PI-SPEC-SW-E309) and the
 * FogBugz event log summary (epic PI-SPEC-SW-E307) with `fogbugz` on some
 * project (without a saved FogBugz key their pages say "Save your FogBugz
 * key"), FogBugz to ADO (epic PI-SPEC-SW-E311) while `fogbugzToAdoVisible`
 * (`fogbugz-to-ado` on some project and a saved FogBugz key) and Product Q&A
 * (epic PI-SPEC-SW-E310) with `ask` on some project.
 * Pure; exported for tests.
 */
export function toolEntries(
  groomingVisible: boolean,
  mayFormulate: boolean,
  mayFogbugz = false,
  mayAsk = false,
  mayBringToAdo = false,
): readonly ToolEntry[] {
  return [
    ...(groomingVisible ? [GROOMING_ENTRY] : []),
    ...(mayFormulate ? [FORMULATOR_ENTRY] : []),
    ...(mayFogbugz ? [FOGBUGZ_FORMULATOR_ENTRY, FOGBUGZ_ENTRY] : []),
    ...(mayBringToAdo ? [FOGBUGZ_TO_ADO_ENTRY] : []),
    ...(mayAsk ? [ASK_ENTRY] : []),
  ];
}

const CURRENT: Record<ToolboxTool, { label: string; icon: LucideIcon }> = {
  grooming: { label: GROOMING_ENTRY.label, icon: GROOMING_ENTRY.icon },
  formulator: { label: FORMULATOR_ENTRY.label, icon: FORMULATOR_ENTRY.icon },
  fogbugz: { label: FOGBUGZ_ENTRY.label, icon: FOGBUGZ_ENTRY.icon },
  "fogbugz-formulator": { label: FOGBUGZ_FORMULATOR_ENTRY.label, icon: FOGBUGZ_FORMULATOR_ENTRY.icon },
  ask: { label: ASK_ENTRY.label, icon: ASK_ENTRY.icon },
  "fogbugz-to-ado": { label: FOGBUGZ_TO_ADO_ENTRY.label, icon: FOGBUGZ_TO_ADO_ENTRY.icon },
  home: { label: "Toolbox home", icon: House },
};

/**
 * The SpecTree Toolbox tool switcher (epic PI-SPEC-SW-E280). Since epic
 * PI-SPEC-SW-E282 it lists Grooming (only while `groomingVisible` is on),
 * the Formulator (with the `formulator` capability somewhere, E283), the
 * FogBugz Formulator (E309) and the FogBugz event log summary (E307) with
 * `fogbugz` somewhere, FogBugz to ADO (E311) with `fogbugz-to-ado`
 * somewhere and a saved FogBugz key, Product Q&A (E310) with `ask` somewhere, and
 * "Toolbox home" (`/toolbox`); the current tool comes
 * from the path. Placement is an open designer question, so it is one
 * self-contained component that can move from the title bar to the rail.
 */
export function ToolSwitcher({ className }: { className?: string }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const mayFormulate = useCanAnywhere("formulator").allowed;
  const mayFogbugz = useCanAnywhere("fogbugz").allowed;
  const mayAsk = useCanAnywhere("ask").allowed;
  const bridge = useToolboxBridge();
  const mayBringToAdo = fogbugzToAdoVisible(
    useCanAnywhere("fogbugz-to-ado").allowed,
    me?.hasFogbugzKey ?? false,
    bridge,
  );
  const tool = toolFromPath(pathname);
  const current = CURRENT[tool];
  const CurrentIcon = current.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        aria-label="Switch tool"
        className={cn(
          "inline-flex h-[26px] items-center gap-1.5 rounded-[7px] px-2 text-[13px] font-medium text-[#D4D4D8] transition-colors hover:bg-[#141417] [-webkit-app-region:no-drag]",
          className
        )}
      >
        <CurrentIcon className={cn("h-3.5 w-3.5", TOOLBOX_TEXT.faint)} aria-hidden="true" />
        {current.label}
        <ChevronDown className={cn("h-[13px] w-[13px]", TOOLBOX_TEXT.faint)} aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {toolEntries(me?.groomingVisible ?? false, mayFormulate, mayFogbugz, mayAsk, mayBringToAdo).map((entry) => {
          const Icon = entry.icon;
          return (
            <DropdownMenuItem
              key={entry.tool}
              className="gap-2"
              onClick={() => {
                void navigate(entry.href);
              }}
            >
              <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="flex-1">{entry.label}</span>
              {tool === entry.tool && <Check className="h-4 w-4 text-primary" aria-label="Current tool" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="gap-2"
          onClick={() => {
            void navigate(TOOLBOX_HOME_PATH);
          }}
        >
          <House className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1">Toolbox home</span>
          {tool === "home" && <Check className="h-4 w-4 text-primary" aria-label="Current tool" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
